import { useEffect } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useToast } from '@sketch/toasts'
import { useModalContext } from '@sketch/components'
import { IntegrationSettingsModal } from '../modals/IntegrationSettingsModal'
import { GetIntegrationConnectUrlMutationVariables } from '@sketch/gql-types'
import { INTEGRATION_INFO } from '../modals/IntegrationSettingsModal/integrationInfo'

// Map hash values to integration providers
const HASH_TO_PROVIDER_MAP: Record<
  string,
  GetIntegrationConnectUrlMutationVariables['provider']
> = {
  slack: 'SLACK',
}

interface UseIntegrationsURLHandlerOptions {
  workspaceId: string
}

export const useIntegrationsURLHandler = ({
  workspaceId,
}: UseIntegrationsURLHandlerOptions) => {
  const location = useLocation()
  const history = useHistory()
  const { showToast } = useToast()
  const { showModal } = useModalContext()

  useEffect(() => {
    // Handle error parameter
    const urlParams = new URLSearchParams(location.search)
    const errorParam = urlParams.get('error')

    if (errorParam === 'integration_failed') {
      showToast(
        'There was an issue setting up your integration, please try again',
        'negative'
      )

      // Clean up the URL by removing the error parameter
      urlParams.delete('error')

      history.replace(location.pathname)

      return
    }

    // Handle hash parameter
    const hash = location.hash.replace('#', '').toLowerCase()

    // Only process if hash exists and matches a provider
    if (hash && HASH_TO_PROVIDER_MAP[hash]) {
      const integrationProvider = HASH_TO_PROVIDER_MAP[hash]

      // Clear the hash first
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: '',
      })

      // Then open the modal
      showModal(IntegrationSettingsModal, {
        integrationProvider,
        workspaceId,
        integrationName: INTEGRATION_INFO[integrationProvider].name,
      })
    }
  }, [
    location.search,
    location.hash,
    location.pathname,
    history,
    showToast,
    showModal,
    workspaceId,
  ])
}
